import { ApiClient } from '../../ApiClient';
import { ConnectionDTO } from '../dto/MarketplaceDTOs';
import { ProductFlowSettings } from '@domain/connection/models/ConnectionSettings';
import { InventorySyncEventItem } from '@domain/connection/models/InventorySyncEvent';
import { FullSyncJob } from '@domain/connection/models/ImportJob';

export class ProductFlowConnectorDataSource {
  constructor(private api: ApiClient) {
  }

  async addConnection(xCompanyId: string, acceptLanguage: string, personalAccessTokenKey: string): Promise<ConnectionDTO> {
    const response = await this.api.post<ConnectionDTO>('/connections/productflow', {
      x_company_id: xCompanyId,
      accept_language: acceptLanguage,
      personal_access_token: personalAccessTokenKey
    });
    return response.data;
  }

  async reconnect(connectionId: string, xCompanyId: string, acceptLanguage: string, personalAccessTokenKey: string): Promise<ConnectionDTO> {
    const response = await this.api.post<ConnectionDTO>(`/connections/productflow/${connectionId}/reconnect`, {
      x_company_id: xCompanyId,
      accept_language: acceptLanguage,
      personal_access_token: personalAccessTokenKey
    });
    return response.data;
  }

  async getMarketplaces(connectionId: string): Promise<{ machine_name: string, connection_id: string, slug: string, name: string }[]> {
    const response = await this.api.get<{ machine_name: string, connection_id: string, slug: string, name: string }[]>(`/connections/productflow/marketplaces?connectionId=${connectionId}`);
    return response.data;
  }

  async fetchWarehouseInventory(connectionId: string): Promise<InventorySyncEventItem[]> {
    const response = await this.api.get<InventorySyncEventItem[]>(`/connections/productflow/inventory/warehouse?connectionId=${connectionId}`);
    return response.data;
  }

  async fetchMarketplaceInventory(connectionId: string, marketplace: string): Promise<InventorySyncEventItem[]> {
    const response = await this.api.get<InventorySyncEventItem[]>(`/connections/productflow/inventory/marketplace/${marketplace}?connectionId=${connectionId}`);
    return response.data;
  }

  async syncAllOrders(connectionId: string): Promise<{ jobId: string }> {
    const response = await this.api.post<{ jobId: string }>(`/connections/productflow/orders/sync/all?connectionId=${connectionId}`);
    return response.data;
  }

  async syncNewOrders(connectionId: string): Promise<{ jobId: string }> {
    const response = await this.api.post<{ jobId: string }>(`/connections/productflow/orders/sync/new?connectionId=${connectionId}`);
    return response.data;
  }

  async syncProducts(connectionId: string): Promise<{ jobId: string }> {
    const response = await this.api.post<{ jobId: string }>(`/connections/productflow/products/sync?connectionId=${connectionId}`);
    return response.data;
  }

  async syncInventory(connectionId: string): Promise<{ jobId: string }> {
    const response = await this.api.post<{ jobId: string }>(`/connections/productflow/inventory/sync?connectionId=${connectionId}`);
    return response.data;
  }

  async syncMarketplaceOffers(connectionId: string): Promise<{ jobId: string }> {
    const response = await this.api.post<{ jobId: string }>(`/connections/productflow/products/marketplace/offers/sync?connectionId=${connectionId}`);
    return response.data;
  }

  async getSettings(connectionId: string): Promise<ProductFlowSettings> {
    const response = await this.api.get<ProductFlowSettings>(`/connections/${connectionId}/settings`);
    return response.data;
  }

  async updateSettings(connectionId: string, settings: Partial<ProductFlowSettings>): Promise<ProductFlowSettings> {
    const response = await this.api.put<ProductFlowSettings>(`/connections/${connectionId}/settings`, settings);
    return response.data;
  }

  async syncFull(connectionId: string): Promise<FullSyncJob[]> {
    const response = await this.api.post<{ jobs: FullSyncJob[] }>(`/connections/productflow/sync/full?connectionId=${connectionId}`);
    return response.data.jobs;
  }
}

